import { makeStyles, styled, Tooltip, withStyles } from "@material-ui/core";
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { useLinked } from "@type-r/react";
import { every, filter, some } from "lodash";
import { div, DragNDropUpload, Flex, navigate, PrimaryButton, SelectFile, span } from "lqx-react";
import { colors } from "lqx-react/src";
import * as React from "react";
import { hasRole } from "src/app/services/can";
import { AssetType, IsOfType } from "../api/assetType";
import { DigiSelect, DigitizationSelect } from "../components/select";
import { DigiCheckbox } from "../shared";
import { DigiSelectAsync } from "../components/select-async";
import { getCompanies } from "../api/company";

export const UploadForm = ({
    $file,
    $userTemplate,
    dataHolders,
    $dataHolderId,
    userTemplates,
    showDownloadTemplate,
    alowedExtensions = "*.csv",
    onUpload,
    modifyExistingAssets,
    mergeExistingAssets,
    onDownloadTemplate,
    onDownloadMapping,
    label,
    showAssetTypes
}
    : {
        $file,
        $userTemplate?,
        dataHolders?: any[],
        $dataHolderId?,
        userTemplates?: any[],
        showDownloadTemplate?: boolean,
        alowedExtensions?: string,
        onUpload,
        modifyExistingAssets?,
        mergeExistingAssets?,
        onDownloadTemplate?,
        onDownloadMapping?,
        label: String,
        showAssetTypes?: boolean
    }) => {
    const hasSelectedFile = $file.value;
    const extensionsArray = alowedExtensions.split(',').map(ext => ext.replace('*.', '').toUpperCase());
    const isValidFile = hasSelectedFile && extensionsArray.filter(ext => $file.value.name.toUpperCase().endsWith(ext)).length > 0;
    const showTemplatesDropdown = useLinked(false);
    const showDataHolderDropdown = useLinked(false);
    const assetTypeOptionMultiDefault = [{ id: AssetType.invoice, name: 'Invoice', disabled: false }, { id: AssetType.creditNote, name: 'Credit Note', disabled: false }, { id: AssetType.unappliedCash, name: 'Unapplied Cash', disabled: false }];
    const $assetTypeOptionMultiList = useLinked(assetTypeOptionMultiDefault);

    const selectedFileExtension = hasSelectedFile ? $file.value.name.split('.').pop() : null;
    const $templatesFilteredByFileExtension = useLinked(null);

    React.useEffect(() => $templatesFilteredByFileExtension
        .set(() => {
            let defaultArray = !showAssetTypes ? [{ id: null, name: 'Default mapping template', assetTypes: [] }] : [];
            if ($multiAssetTypes.value && $multiAssetTypes.value.length > 1) {
                return getFilterTemplatesByAssetTypes($multiAssetTypes.value);
            }
            return [...defaultArray, ...userTemplates.filter((template) => template.dataFormat === `.${selectedFileExtension}`)];
        }), [$file]);

    const defaultImportTemplate = userTemplates ? userTemplates.filter((template) => template.isDefault)[0] : null;

    const $multiAssetTypes = useLinked([]).onChange(values => {
        if (values && values.find(x => x.id === AssetType.creditNote)) {
            $assetTypeOptionMultiList.value.find(x => x.id == AssetType.unappliedCash).disabled = true;
            $assetTypeOptionMultiList.set($assetTypeOptionMultiList.value);
        }
        if (values && values.find(x => x.id === AssetType.unappliedCash)) {
            $assetTypeOptionMultiList.value.find(x => x.id == AssetType.creditNote).disabled = true;
            $assetTypeOptionMultiList.set($assetTypeOptionMultiList.value);
        }
        if (!values || (values.length == 1 && values.find(x => x.id === AssetType.invoice))) {
            $assetTypeOptionMultiList.set(assetTypeOptionMultiDefault);
            $templatesFilteredByFileExtension.set(userTemplates);
        }
        values && values.length > 1 && getFilterTemplatesByAssetTypes(values);
        $userTemplate.set({ id: null });
    });

    const getFilterTemplatesByAssetTypes = (values) =>
        $templatesFilteredByFileExtension.set(filter(userTemplates,
            t => {
                if (!t.id) return true;
                return every(values, object => some(t.assetTypes.models, { id: object.id })) && t.dataFormat === `.${selectedFileExtension}`;
            }));

    React.useMemo(() => {
        if ($userTemplate) {
            const hasDefaulteTemplate = $templatesFilteredByFileExtension && $templatesFilteredByFileExtension.value
                ? $templatesFilteredByFileExtension.value.find((template => template.isDefault))
                : null;

            $userTemplate.set(hasDefaulteTemplate ?
                { id: hasDefaulteTemplate.id } :
                { id: null, name: 'Default mapping template', assetTypes: [] });
        }
    }, [$file ? $file['_changeToken'] : true]);

    const isAllowFileExtension = hasSelectedFile && (($file.value.name as string).toUpperCase().endsWith('PDF') 
                                                        || ($file.value.name as string).toUpperCase().endsWith('TIF')
                                                        || ($file.value.name as string).toUpperCase().endsWith('MSG'));

    const showTemplateContainer = hasSelectedFile && !isAllowFileExtension;
    const showDataHolderContainer = hasRole('Administrator') && hasSelectedFile;

    return (
        <UploadContainer>
            <Flex row alignCenter >
                <UploadTitle>Load {label} File</UploadTitle>
            </Flex>
            {hasSelectedFile && !isValidFile && <UploadWarning>Your file is in incorrect format1</UploadWarning>}
            <DragNDropUploadSection $file={$file} />
            {$file.value &&
                <Flex column center>
                    <UploadedFileName>{$file.value.name}</UploadedFileName>
                </Flex>}
            <Flex column center>
                <span>Drag and Drop your File ({extensionsArray.join(', ')}) or <UploadSelectFile lqx-qa-id="upload invoice file" label="select a file" $value={$file} accept={alowedExtensions} /> to Upload.</span>
            </Flex>
            {showAssetTypes &&
                <Flex column center>
                    <AssetTypesLabel>Asset Types</AssetTypesLabel>
                    <DigitizationSelectContainer>
                        <DigitizationSelect
                            placeholder={'Asset Type'}
                            $value={$multiAssetTypes}
                            options={$assetTypeOptionMultiList.value}
                            isMulti={true}
                        />
                    </DigitizationSelectContainer>
                </Flex>}
            {onDownloadTemplate &&
                <DownloadTemplateContainer>
                    {showDownloadTemplate &&
                        <Flex row >
                            <span >
                                {defaultImportTemplate && defaultImportTemplate.isDefault
                                    ? <NavigateTemplateButton onClick={() => navigate(`import-templates/edit/${defaultImportTemplate.id}`)}>Using configured Import Template: {defaultImportTemplate.name}</NavigateTemplateButton>
                                    : <DownloadTemplateButton onClick={onDownloadTemplate}>Standard {label} .csv Format</DownloadTemplateButton>
                                }
                            </span>
                        </Flex>
                    }
                    <Flex column stretch>
                        {showTemplateContainer && (<UserTemplateContainer>
                            <Flex row>
                                <ChooseButton color="blue" disabled={!isValidFile} onClick={() => showTemplatesDropdown.set(!showTemplatesDropdown.value)}>
                                    Choose Template
                                </ChooseButton>
                            </Flex>
                            {($templatesFilteredByFileExtension && $templatesFilteredByFileExtension.value && showTemplatesDropdown.value) && (
                                <DropDownSection column stretch>
                                    <Flex row >You are currently using the following mapping template:</Flex>
                                    <Flex row >
                                        <DigiSelectContainer>
                                            <DigiSelect data-qa-id="data template" id="id" label="name" $value={$userTemplate} options={$templatesFilteredByFileExtension.value} />
                                        </DigiSelectContainer>
                                        <Tooltip title="Download mapping template sample">
                                            <StyledCloudDownloadIcon color="primary" onClick={onDownloadMapping} />
                                        </Tooltip>
                                    </Flex>
                                </DropDownSection>)
                            }
                        </UserTemplateContainer>)}
                    </Flex>
                    <Flex column stretch>
                        {showDataHolderContainer && (<UserTemplateContainer>
                            <Flex row >
                                <ChooseButton color="blue" disabled={!isValidFile} onClick={() => showDataHolderDropdown.set(!showDataHolderDropdown.value)}>
                                    Choose Data Owner
                                </ChooseButton>
                            </Flex>
                            {showDataHolderDropdown.value && (
                                <DropDownSection column stretch>
                                    <Flex row >You are currently using the following data owner:</Flex>
                                    <Flex row >
                                        <DigiSelectContainer>
                                            <DigiSelectAsync data-qa-id="data owner" id="id" label="name" getItems={getCompanies} $value={$dataHolderId} />
                                        </DigiSelectContainer>
                                    </Flex>
                                </DropDownSection>)
                            }
                        </UserTemplateContainer>)
                        }
                    </Flex>
                </DownloadTemplateContainer>
            }
            <Flex column>
                {
                    IsOfType(label, AssetType.invoice) ?
                        <ModifyAssetCheckBox>
                            <DigiCheckbox label="Modify Existing Assets" $checked={modifyExistingAssets}></DigiCheckbox>
                        </ModifyAssetCheckBox> : <></>
                }
                                {
                    IsOfType(label, AssetType.remittanceAdvice) ?
                        <ModifyAssetCheckBox>
                            <DigiCheckbox label="Merge RA Line Items" $checked={mergeExistingAssets}></DigiCheckbox>
                        </ModifyAssetCheckBox> : <></>
                }
                <UploadButton disabled={!hasSelectedFile || !isValidFile} onClick={onUpload}>Upload</UploadButton>
            </Flex>
        </UploadContainer>
    );
}

const ModifyAssetCheckBox = div({
    marginTop: '30px',
    marginLeft: 'auto',
    float: 'right',
    '& span': {
        padding: '0px !important',
        fontSize: '9pt',
        marginRight: '5px',
        fontWeight: 500
    },

})

const UploadContainer = div({
    backgroundColor: 'white',
    padding: '30px'
});

const UploadTitle = div({
    fontFamily: 'Montserrat',
    color: '#4197d6',
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '20px',
    marginBottom: '15px'
});

const UploadWarning = div({
    marginTop: '15px',
    color: '#fccb4f'
});

const UploadedFileName = div({
    color: '#4197d6',
    fontWeight: 800,
    wordBreak: 'break-word'
});

const DownloadTemplateContainer = div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'left',
    marginTop: '15px',
    paddingTop: '15px',
    paddingBottom: '15px'
});

const DownloadTemplateButton = span({
    color: colors.blue,
    cursor: 'pointer',
    fontWeight: 500
});

const NavigateTemplateButton = span({
    color: colors.blue,
    cursor: 'pointer',
    fontWeight: 500
});

const UploadButton = styled(PrimaryButton)({
    marginTop: '5px',
    color: 'white',
    border: 'none',
    marginLeft: 'auto'
});

const ChooseButton = styled(PrimaryButton)({
    margin: '0px'
});

const UploadSelectFile = styled(SelectFile)({
    border: 'none',
    display: 'inline-block',
    padding: 0,
    '& .filename': {
        display: 'none'
    },
    '& .MuiSvgIcon-root': {
        display: 'none'
    },
    '& div': {
        color: '#4197d6',
        fontSize: 15
    },
    cursor: 'pointer'
});

const UserTemplateContainer = div({
    paddingTop: '20px'
});

const DigiSelectContainer = div({
    width: '650px'
})

const StyledCloudDownloadIcon = withStyles({
    root: {
        margin: '0 0 0 13px',
        cursor: 'pointer'
    },
    colorPrimary: {
        fontSize: 'xx-large',
        color: '#0099e1'
    }
})(CloudDownloadIcon);

const DigitizationSelectContainer = styled(Flex)({
    width: '500px'
});

const AssetTypesLabel = div({
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '13px',
    padding: '4px',
    marginTop: '5px',
    color: 'grey'
});

const DropDownSection = styled(Flex)({
    margin: '8px 0'
})

const DragNDropUploadSection = ({ $file }) => {
    const classes = useDragNDropUploadStyles();
    return <div className={classes.root}><DragNDropUpload $file={$file} /></div>
}

const useDragNDropUploadStyles = makeStyles({
    root: {
        '& [class*=Component-root-]': {
            padding: '5px'
        }
    }
})